import React from "react";
import {
    SelectItem,
    SharedSelection,
} from "@heroui/react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { Tooltip } from "@heroui/react";
import AppSelect from "../../../components/common/appSelect";

interface SportInformationProps {
    t: any;
    selectedSport: string;
    setSelectedSport: (value: string) => void;
    setSport: (value: string) => void;
    sports: string[];
    errors: any;
    setErrors: (value: any) => void;
    loadSportsIfNeeded: () => Promise<void>;
    formatWeightClass: (weightClass: string) => string;
    preferredRuleSet: string;
    setPreferredRuleSet: (value: string) => void;
    selectedWeight: string;
    setSelectedWeight: (value: string) => void;
    setWeightClass: (value: string) => void;
    weightClasses: string[];
    loadWeightClassesIfNeeded: () => Promise<void>;
    selectedKey: string | undefined;
    handleChange: (keys: SharedSelection) => void;
    monthOptions: { label: string; months: number }[];
}

const SportInformation: React.FC<SportInformationProps> = ({
    t,
    selectedSport,
    setSelectedSport,
    setSport,
    sports,
    errors,
    setErrors,
    loadSportsIfNeeded,
    formatWeightClass,
    preferredRuleSet,
    setPreferredRuleSet,
    selectedWeight,
    setSelectedWeight,
    setWeightClass,
    weightClasses,
    loadWeightClassesIfNeeded,
    selectedKey,
    handleChange,
    monthOptions,
}) => {
    return (
        <div className="space-y-2">
            <h3 className="text-xs font-semibold text-gray-800 border-b pb-1">
                {t("sport_information")}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-600 block">
                        <div className="flex items-center gap-1">
                            {t("sports")}
                            <Tooltip
                                className="w-[350px]"
                                placement="right"
                                content={
                                    <div className="text-sm">
                                        <span className="font-semibold block mb-1">
                                            {t("sports_tooltip_title")}
                                        </span>
                                        <span>
                                            {t("sports_tooltip_description")}
                                        </span>
                                    </div>
                                }
                            >
                                <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-pointer" />
                            </Tooltip>
                        </div>
                    </label>
                    <AppSelect
                        placeholder={t("select_sport_placeholder")}
                        selectedKeys={selectedSport ? [selectedSport] : []}
                        onSelectionChange={(key) => {
                            const selected = Array.from(key)[0] as string;
                            setSelectedSport(selected);
                            setSport(selected);
                            setErrors((prev: any) => ({
                                ...prev,
                                sport: selected ? "" : t("sport_required"),
                            }));
                        }}
                        onOpenChange={(isOpen) =>
                            isOpen && loadSportsIfNeeded?.()
                        }
                        className="w-full"
                        size="sm"
                    >
                        {sports.length > 0 ? (
                            sports.map((sport) => (
                                <SelectItem key={sport}>
                                    {formatWeightClass(sport)}
                                </SelectItem>
                            ))
                        ) : (
                            <SelectItem key="no-sport" isDisabled>
                                {t("no_sports_available")}
                            </SelectItem>
                        )}
                    </AppSelect>
                    {errors.sport && (
                        <div className="text-red-500 text-xs">
                            {errors.sport}
                        </div>
                    )}
                </div>
                <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-600 block">
                        <div className="flex items-center gap-1">
                            {t("level")}
                            <Tooltip
                                className="w-[350px]"
                                placement="right"
                                content={
                                    <div className="text-sm">
                                        <span className="font-semibold block mb-1">
                                            {t("level_tooltip_title")}
                                        </span>
                                        <span>
                                            {t("level_tooltip_description")}
                                        </span>
                                    </div>
                                }
                            >
                                <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-pointer" />
                            </Tooltip>
                        </div>
                    </label>
                    <AppSelect
                        placeholder={t("select_level_placeholder")}
                        selectedKeys={
                            preferredRuleSet ? [preferredRuleSet] : []
                        }
                        onSelectionChange={(keys) => {
                            const selected = Array.from(keys)[0] as string;
                            setPreferredRuleSet(selected);
                            setErrors((prev: any) => ({
                                ...prev,
                                preferredRuleSet: selected
                                    ? ""
                                    : t("level_required"),
                            }));
                        }}
                        size="sm"
                        className="w-full"
                        classNames={{
                            trigger: "min-h-[32px] h-8",
                        }}
                    >
                        <SelectItem key="AMETEUR">{t("amateur")}</SelectItem>
                        <SelectItem key="SEMI_PRO">{t("semi_pro")}</SelectItem>
                        <SelectItem key="PROFESSIONAL">{t("professional")}</SelectItem>
                    </AppSelect>
                    {errors.preferredRuleSet && (
                        <div className="text-red-500 text-xs">
                            {errors.preferredRuleSet}
                        </div>
                    )}
                </div>
                <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-600 block">
                        <div className="flex items-center gap-1">
                            {t("weight_class")}
                            <Tooltip
                                className="w-[350px]"
                                placement="right"
                                content={
                                    <div className="text-sm">
                                        <span className="font-semibold block mb-1">
                                            {t("weight_class_tooltip_title")}
                                        </span>
                                        <span>
                                            {t("weight_class_tooltip_description")}
                                        </span>
                                    </div>
                                }
                            >
                                <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-pointer" />
                            </Tooltip>
                        </div>
                    </label>
                    <AppSelect
                        placeholder={t("select_weight_class_placeholder")}
                        selectedKeys={selectedWeight ? [selectedWeight] : []}
                        onSelectionChange={(key) => {
                            const selected = Array.from(key)[0] as string;
                            setSelectedWeight(selected);
                            setWeightClass(selected);
                            setErrors((prev: any) => ({
                                ...prev,
                                weightClass: selected
                                    ? ""
                                    : t("weight_class_required"),
                            }));
                        }}
                        onOpenChange={(isOpen) =>
                            isOpen && loadWeightClassesIfNeeded?.()
                        }
                        className="w-full"
                        size="sm"
                    >
                        {weightClasses.length > 0 ? (
                            weightClasses.map((weight) => (
                                <SelectItem key={weight}>
                                    {formatWeightClass(weight)}
                                </SelectItem>
                            ))
                        ) : (
                            <SelectItem key="no-weight" isDisabled>
                                {t("no_weight_classes_available")}
                            </SelectItem>
                        )}
                    </AppSelect>
                    {errors.weightClass && (
                        <div className="text-red-500 text-xs">
                            {errors.weightClass}
                        </div>
                    )}
                </div>
                <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-600 block">
                        <div className="flex items-center gap-1">
                            {t("valid_upto")}
                            <Tooltip
                                className="w-[350px]"
                                placement="right"
                                content={
                                    <div className="text-sm">
                                        <span className="font-semibold block mb-1">
                                            {t("valid_upto_tooltip_title")}
                                        </span>
                                        <span>
                                            {t("valid_upto_tooltip_description")}
                                        </span>
                                    </div>
                                }
                            >
                                <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-pointer" />
                            </Tooltip>
                        </div>
                    </label>
                    <AppSelect
                        size="sm"
                        radius="lg"
                        placeholder={t("select_valid_upto_placeholder")}
                        className="w-full text-xs"
                        selectedKeys={
                            selectedKey ? new Set([selectedKey]) : undefined
                        }
                        onSelectionChange={handleChange}
                    >
                        {monthOptions.map((option) => (
                            <SelectItem key={option.months}>
                                {option.label}
                            </SelectItem>
                        ))}
                    </AppSelect>
                    {errors.validUpto && (
                        <div className="text-red-500 text-xs">
                            {errors.validUpto}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SportInformation;
